import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";

export interface Profile {
  first_name: string;
  last_name: string;
  phone: string;
  address_line: string;
  city: string;
  country: string;
  postal_code: string;
}

const emptyProfile: Profile = {
  first_name: "",
  last_name: "",
  phone: "",
  address_line: "",
  city: "",
  country: "",
  postal_code: "",
};

export function useProfile() {
  const { user } = useAuth();
  const [profile, setProfile] = useState<Profile>(emptyProfile);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user) {
      setProfile(emptyProfile);
      setLoading(false);
      return;
    }

    const fetchProfile = async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from("profiles")
        .select("first_name, last_name, phone, address_line, city, country, postal_code")
        .eq("user_id", user.id)
        .maybeSingle();

      if (data && !error) {
        setProfile({
          first_name: data.first_name || "",
          last_name: data.last_name || "",
          phone: data.phone || "",
          address_line: data.address_line || "",
          city: data.city || "",
          country: data.country || "",
          postal_code: data.postal_code || "",
        });
      }
      setLoading(false);
    };

    fetchProfile();
  }, [user]);

  const updateProfile = useCallback(async (updates: Partial<Profile>) => {
    if (!user) return { error: new Error("Not authenticated") };
    setSaving(true);
    const { error } = await supabase
      .from("profiles")
      .update(updates)
      .eq("user_id", user.id);

    if (!error) {
      setProfile((prev) => ({ ...prev, ...updates }));
    }
    setSaving(false);
    return { error };
  }, [user]);

  return { profile, loading, saving, updateProfile };
}
